import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { css, html } from '../../lib/lit.js';

// The signups channel is a single setting, not a list: one Discord channel that
// confirmed early-access signups are posted to. Unset, they go to #d3-staff.
class SignupsChannelView extends BaseComponent {
    static properties = {
        _channel: { type: Object, state: true },
        _loading: { type: Boolean, state: true },
        _error: { type: String, state: true },
        _notice: { type: String, state: true },
        _channelID: { type: String, state: true },
        _saving: { type: Boolean, state: true },
    };

    static styles = css`
        :host { display: block; }
    `;

    constructor() {
        super();
        this._channel = null;
        this._loading = true;
        this._error = null;
        this._notice = null;
        this._channelID = '';
        this._saving = false;
    }

    async connectedCallback() {
        super.connectedCallback();
        await this._load();
    }

    async _load() {
        this._loading = true;
        this._error = null;
        try {
            this._setChannel(await api.get('/api/signups-channel'));
        } catch (err) {
            this._error = err.message;
        } finally {
            this._loading = false;
        }
    }

    _setChannel(channel) {
        this._channel = channel;
        this._channelID = channel?.DiscordChannelID ?? '';
    }

    async _save(e) {
        e.preventDefault();
        this._saving = true;
        this._error = null;
        this._notice = null;
        try {
            this._setChannel(
                await api.put('/api/signups-channel', {
                    discord_channel_id: this._channelID.trim(),
                }),
            );
            this._notice = 'Saved.';
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    async _clear() {
        this._error = null;
        this._notice = null;
        try {
            await api.delete('/api/signups-channel');
            this._setChannel(null);
            this._notice = 'Cleared. Signups will post to #d3-staff.';
        } catch (err) {
            this._error = err.message;
        }
    }

    async _test() {
        this._error = null;
        this._notice = null;
        try {
            await api.post('/api/signups-channel/test');
            this._notice = 'Test notification sent.';
        } catch (err) {
            this._error = err.message;
        }
    }

    render() {
        if (this._loading) {
            return html`<div class="text-center py-4"><span class="spinner-border"></span></div>`;
        }

        return html`
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0">Signups Channel</h5>
                ${
                    this._channel
                        ? html`
                    <div class="d-flex gap-2">
                        <button class="btn btn-outline-info btn-sm" title="Send test notification" @click="${this._test}">
                            <i class="bi bi-send"></i> Test
                        </button>
                        <button class="btn btn-outline-danger btn-sm" title="Clear the signups channel" @click="${this._clear}">
                            <i class="bi bi-x-circle"></i> Clear
                        </button>
                    </div>
                `
                        : ''
                }
            </div>

            <p class="text-muted" style="font-size: 0.875rem;">
                Every confirmed early-access signup from the community portal is posted to this
                channel. With no channel set, or if posting here fails, signups go to #d3-staff.
            </p>

            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}
            ${this._notice ? html`<div class="alert alert-success">${this._notice}</div>` : ''}

            <form @submit="${(e) => this._save(e)}" style="max-width: 500px;">
                <div class="mb-3">
                    <label class="form-label">Discord Channel ID</label>
                    <input type="text" class="form-control" placeholder="e.g. 1234567890"
                           .value="${this._channelID}"
                           @input="${(e) => {
                               this._channelID = e.target.value;
                           }}"
                           required>
                    <div class="form-text">Enable Developer Mode in Discord settings, then right-click the channel and Copy Channel ID</div>
                </div>

                <button type="submit" class="btn btn-primary" ?disabled="${this._saving}">
                    ${this._saving ? html`<span class="spinner-border spinner-border-sm"></span>` : 'Save'}
                </button>
            </form>
        `;
    }
}

customElements.define('signups-channel-view', SignupsChannelView);
